import ReduxStore from '../../../ReduxStore';
import {makeTargetProperty, makeTargetPropertySection} from 'api/reports/cma/TargetPropertySectionFactory';
import {resizePropertyImages} from 'lib/ReportUtil';
import {checkIncluded, sortedSections} from 'ducks/reportSections';
import {filteredSection} from 'api/reports/cma/SectionsFilteringUtil';
import {makeCoverLetterSection} from 'api/reports/cma/CoverLetterSectionFactory';
import {makeTestimonialsSection} from 'api/reports/cma/TestimonialsSectionFactory';
import {makeSuburbProfileSection} from 'api/reports/cma/SuburbProfileSectionFactory';
import {makeComparableItemMapSection} from 'api/reports/cma/ComparableItemMapSectionFactory';
import {makeComparablePropertySection} from 'api/reports/cma/ComparablePropertySectionFactory';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {insertFloorPlanSections} from 'api/reports/cma/FloorPlanSectionsFactory';
import {insertFeaturedPropertyPages} from 'api/reports/cma/FeaturedPropertyPagesFactory';
import {makeSummarySection} from 'api/reports/cma/SummarySectionFactory';
import {makeComparableSection} from 'api/reports/cma/ComparableSectionFactory';
import {encodedUriWithoutResize} from 'api/reports/util/UriUtils';
import toUpper from 'lodash/toUpper';
import makeCoverSection from 'api/reports/cma/CoverSectionFactory';
import {makeNzTitlesSection} from 'api/reports/cma/NzTitlesSectionFactory';
import RapidUtils from 'lib/RapidUtils';

export const getCMAReportPayload = (reportType) => () => {
  const {
    propertyDetail,
    userDetails,
    comparableListings,
    comparableSales,
    comparableRentals,
    reportSections: {[reportType]: sectionsForReportType},
    propertyNotes,
    theme,
    featureFlags,
    userSettings,
    testimonials,
    nzTitles,
  } = ReduxStore.getState();

  try {
    let targetProperty = makeTargetProperty(sectionsForReportType, propertyDetail);
    targetProperty = resizePropertyImages(targetProperty);

    const sections = sortedSections(sectionsForReportType)
      .filter(s => checkIncluded(s, {comparableSales, comparableListings, comparableRentals}))
      .filter(s => filteredSection({section: s, data: {comparableSales, comparableListings, comparableRentals}}))
      .filter(s => (s.key === 'TESTIMONIALS' && testimonials[reportType].length > 0) || s.key !== 'TESTIMONIALS')
      .filter(s => s.key !== 'NZ_TITLES' || featureFlags.nzTitleReportEnabled)
      .filter(s => !s.hasError)
      .map(s => {
        switch (s.key) {
          case 'COVER_PAGE':
            return makeCoverSection(s);
          case 'COVER_LETTER':
            return makeCoverLetterSection(s, userDetails, propertyDetail);
          case 'TARGET_PROPERTY':
            return makeTargetPropertySection(s);
          case 'TESTIMONIALS':
            return makeTestimonialsSection(s, testimonials[reportType]);
          case 'SUBURB_PROFILE':
            return makeSuburbProfileSection(s, reportType);
          case 'COMPARABLE_SALES_MAP':
          case 'COMPARABLE_LISTINGS_MAP':
          case 'COMPARABLE_RENTALS_MAP':
            return makeComparableItemMapSection(s, reportType);
          case 'COMPARABLE_SALES':
          case 'COMPARABLE_LISTINGS':
          case 'COMPARABLE_RENTALS':
            return makeComparablePropertySection(s);
          case 'NZ_TITLES':
            return makeNzTitlesSection(s, nzTitles);
          default:
            return {
              name: s.key,
              title: get(s, 'sectionData.title'),
              pdf: s.url,
              data: isEmpty(s.sectionData) ? undefined : s.sectionData,
            };
        }
      });

    insertFloorPlanSections(sectionsForReportType, sections);
    insertFeaturedPropertyPages(sectionsForReportType, sections, {
      comparableListings,
      comparableSales,
      comparableRentals
    });

    const summary = makeSummarySection(sectionsForReportType);
    const selectedSales = reportType !== 'rental' ? makeComparableSection('sales', comparableSales, featureFlags, userSettings, propertyNotes, featureFlags.enableUndisclosedPriceInCMAReport) : undefined;
    const selectedListings = reportType !== 'rental' ? makeComparableSection('listings', comparableListings, featureFlags, userSettings, propertyNotes) : undefined;
    const selectedRentals = reportType === 'rental' ? makeComparableSection('rentals', comparableRentals, featureFlags, userSettings, propertyNotes) : undefined;

    return {
      header: {
        template: 'signature/cma/2.0/header',
        data: {
          agencyLogoUri: encodedUriWithoutResize(userDetails.agencyLogoUri),
          theme,
        }
      },
      sections,
      userDetails,
      comparableSales: selectedSales,
      comparableListings: RapidUtils.sanitizeListings('listings', selectedListings),
      comparableRentals: RapidUtils.sanitizeListings('rentals', selectedRentals),
      propertyDetail: targetProperty,
      summary,
      reportType: toUpper(reportType),
    };
  } catch (e) {
    console.log(e);
    throw e;
  }
};
